import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, DollarSign, Target, Percent, Download, Calendar } from 'lucide-react';
import TopVendedores from './TopVendedores';

const COLORS = ['#f6c90e','#60a5fa','#4ade80','#fb923c','#c084fc','#f87171','#2dd4bf'];

const TOOLTIP_STYLE = {
  contentStyle: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:8 },
  labelStyle:   { color:'#e2e8f0', fontWeight:600 },
  itemStyle:    { color:'#e2e8f0' },
};

// Tooltip customizado para o gráfico de barras + linha
function BarLineTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const leads      = payload.find(p => p.dataKey === 'count')?.value ?? 0;
  const fechados   = payload.find(p => p.dataKey === 'fechados')?.value ?? 0;
  const receita    = payload.find(p => p.dataKey === 'receita_fechada')?.value ?? 0;
  return (
    <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, padding:'10px 14px' }}>
      <div style={{ color:'#e2e8f0', fontWeight:600, marginBottom:6 }}>{label}</div>
      <div style={{ color:'#f6c90e', fontSize:12, marginBottom:3 }}>Leads: {leads}</div>
      <div style={{ color:'#93c5fd', fontSize:12, marginBottom: receita > 0 ? 3 : 0 }}>Vendas fechadas: {fechados}</div>
      {receita > 0 && <div style={{ color:'#4ade80', fontSize:12 }}>Receita fechada: R$ {(receita / 1000).toFixed(1)}k</div>}
    </div>
  );
}

// Filtro de meses estilo slicer do Excel
function MesFiltro({ meses, selected, onChange }) {
  const porAno = {};
  meses.forEach(m => {
    if (!porAno[m.year]) porAno[m.year] = [];
    porAno[m.year].push(m);
  });
  const anos = Object.keys(porAno).map(Number).sort();
  const isTodos = selected.length === 0;

  const toggleMonth = ym => {
    if (selected.includes(ym)) onChange(selected.filter(m => m !== ym));
    else onChange([...selected, ym]);
  };

  const toggleYear = year => {
    const yearMs = porAno[year].map(m => m.ym);
    const allSel = yearMs.every(m => selected.includes(m));
    if (allSel) onChange(selected.filter(m => !yearMs.includes(m)));
    else onChange([...new Set([...selected, ...yearMs])]);
  };

  const chip = (active, color = '#f6c90e') => ({
    padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', border: 'none',
    fontWeight: active ? 700 : 400,
    background: active ? color : 'rgba(255,255,255,0.04)',
    color: active ? (color === '#f6c90e' ? '#080c14' : '#e2e8f0') : '#64748b',
    outline: active ? `2px solid ${color}` : 'none',
    outlineOffset: -1,
  });

  return (
    <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:6 }}>
        <button onClick={() => onChange([])} style={chip(isTodos)}>Todos o período</button>
        {anos.length > 0 && <div style={{ width:1, height:22, background:'#1a2035', margin:'0 4px' }} />}
        {anos.map(year => {
          const yearMs = porAno[year].map(m => m.ym);
          const allSel = yearMs.every(m => selected.includes(m));
          const someSel = !allSel && yearMs.some(m => selected.includes(m));
          return (
            <div key={year} style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
              <button
                onClick={() => toggleYear(year)}
                style={{ ...chip(allSel || someSel, '#60a5fa'), fontWeight:700 }}
              >{year}</button>
              {porAno[year].map(m => (
                <button key={m.ym} onClick={() => toggleMonth(m.ym)} style={chip(selected.includes(m.ym))}>
                  {m.label}
                </button>
              ))}
              <div style={{ width:1, height:22, background:'#1a2035', margin:'0 2px' }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:28 },
  kpiGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:28 },
  kpi: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:'20px 22px' },
  kpiLabel: { fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, marginBottom:8 },
  kpiValue: { fontSize:28, fontWeight:800 },
  chartsRow: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:16 },
  card: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:20 },
  cardTitle: { fontSize:14, fontWeight:600, color:'#cbd5e1', marginBottom:16 },
};

function KPI({ label, value, color='#f6c90e', icon: Icon }) {
  return (
    <div style={S.kpi}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={S.kpiLabel}>{label}</div>
        {Icon && <Icon size={18} color={color} />}
      </div>
      <div style={{ ...S.kpiValue, color }}>{value}</div>
    </div>
  );
}

// Helpers de data
const _fmtDDMM = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
// Retorna YYYY-MM-DD no fuso de Brasília (evita divergência UTC vs BRT após 21h)
const _toISO   = (d) => {
  try { return d.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }); }
  catch { return d.toISOString().slice(0, 10); }
};

// Conversão dd/mm/aaaa ↔ yyyy-mm-dd e máscara numérica
const _isoToDDMM = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};
const _ddmmToISO = (ddmm) => {
  if (!ddmm || ddmm.length < 10) return '';
  const parts = ddmm.split('/');
  if (parts.length < 3 || parts[2].length < 4) return '';
  const [d, m, y] = parts;
  const dd = parseInt(d, 10), mm = parseInt(m, 10);
  if (isNaN(dd) || isNaN(mm) || mm < 1 || mm > 12 || dd < 1 || dd > 31) return '';
  return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
};
const _maskDate = (val) => {
  const digits = val.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}/${digits.slice(2)}`;
  return `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
};

// Input de data compacto: dd/mm/aaaa + botão calendário
// O input date é sobreposto ao ícone para funcionar nativamente em iOS/Android/Safari
// sem precisar de showPicker() (não suportado no Safari iOS antigo).
function DatePickerInput({ value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center' }}>
      <input
        type="text"
        placeholder="dd/mm/aaaa"
        maxLength={10}
        value={value}
        onChange={e => onChange(_maskDate(e.target.value))}
        style={{ padding:'8px 0 8px 8px', background:'transparent', border:'none', color: value ? '#e2e8f0' : '#475569', fontSize:12, outline:'none', width:90 }}
      />
      <div style={{ position:'relative', display:'flex', alignItems:'center', padding:'0 8px 0 2px' }}>
        <Calendar size={13} color="#475569" style={{ pointerEvents:'none' }} />
        <input
          type="date"
          tabIndex={-1}
          style={{ position:'absolute', inset:0, opacity:0, fontSize:16, cursor:'pointer', width:'100%', height:'100%' }}
          onChange={e => e.target.value && onChange(_isoToDDMM(e.target.value))}
        />
      </div>
    </div>
  );
}

function DashboardChefe() {
  const [data, setData]           = useState(null);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro]           = useState('');

  // Filtro de intervalo de datas (aplicado → dispara fetch)
  const [filtroInicio, setFiltroInicio] = useState('');
  const [filtroFim,    setFiltroFim]    = useState('');
  // Valores dos inputs (editados pelo usuário antes de clicar Buscar)
  const [inputInicio, setInputInicio] = useState('');
  const [inputFim,    setInputFim]    = useState('');

  // Recarrega dados quando o filtro aplicado muda
  useEffect(() => {
    setData(null);
    setErro('');
    const p = new URLSearchParams();
    if (filtroInicio) p.set('data_inicio', filtroInicio);
    if (filtroFim)    p.set('data_fim',    filtroFim);
    const qs = p.toString() ? `?${p}` : '';
    api.get(`/dashboard/chefes/${qs}`)
      .then(r => setData(r.data))
      .catch(() => setErro('Erro ao carregar dados. Tente atualizar a página.'));
  }, [filtroInicio, filtroFim]);

  const handleExportarDados = async () => {
    setExportando(true);
    try {
      const res = await api.get('/exportar-dados/', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const hoje = new Date();
      const dd = String(hoje.getDate()).padStart(2, '0');
      const mm = String(hoje.getMonth() + 1).padStart(2, '0');
      const yyyy = hoje.getFullYear();
      const a = document.createElement('a');
      a.href = url;
      a.download = `Relatorio E-solar ${dd}_${mm}_${yyyy}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erro ao exportar dados. Tente novamente.');
    } finally {
      setExportando(false);
    }
  };

  // Top 5 status para o donut
  const pieData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.leads_por_status)
      .map(([name, value]) => ({ name, value }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [data]);

  const totalPie = useMemo(() => pieData.reduce((s, d) => s + d.value, 0), [pieData]);

  // Tooltip do donut com % — definido aqui para acesso ao totalPie via closure
  const PieTooltipContent = useMemo(() => {
    return function PieTip({ active, payload }) {
      if (!active || !payload?.[0]) return null;
      const d = payload[0];
      const pct = totalPie > 0 ? ((d.value / totalPie) * 100).toFixed(1) : 0;
      return (
        <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, padding:'10px 14px' }}>
          <div style={{ color:'#e2e8f0', fontWeight:600, fontSize:12 }}>{d.name}</div>
          <div style={{ color:'#94a3b8', fontSize:12 }}>{d.value} leads · {pct}%</div>
        </div>
      );
    };
  }, [totalPie]);

  // Datas calculadas para os botões de atalho
  const _hoje       = new Date();
  const _semAtr     = new Date(_hoje); _semAtr.setDate(_hoje.getDate() - 7);
  const _mesAtr     = new Date(_hoje); _mesAtr.setMonth(_hoje.getMonth() - 1);
  const _isoHoje    = _toISO(_hoje);
  const _isoSemAtr  = _toISO(_semAtr);
  const _isoMesAtr  = _toISO(_mesAtr);

  const aplicarSemanal = () => {
    setInputInicio(_isoToDDMM(_isoSemAtr)); setInputFim(_isoToDDMM(_isoHoje));
    setFiltroInicio(_isoSemAtr); setFiltroFim(_isoHoje);
  };
  const aplicarMensal = () => {
    setInputInicio(_isoToDDMM(_isoMesAtr)); setInputFim(_isoToDDMM(_isoHoje));
    setFiltroInicio(_isoMesAtr); setFiltroFim(_isoHoje);
  };
  const buscar = () => { setFiltroInicio(_ddmmToISO(inputInicio)); setFiltroFim(_ddmmToISO(inputFim)); };

  const ativoSem = filtroInicio === _isoSemAtr && filtroFim === _isoHoje;
  const ativoMes = filtroInicio === _isoMesAtr && filtroFim === _isoHoje;

  const btnAtalho = (ativo) => ({
    background: ativo ? 'rgba(246,201,14,0.12)' : 'transparent',
    color: ativo ? '#f6c90e' : '#64748b',
    border: `1px solid ${ativo ? 'rgba(246,201,14,0.35)' : '#1a2035'}`,
    borderRadius:8, padding:'8px 13px', fontSize:12, fontWeight:600,
    cursor:'pointer', whiteSpace:'nowrap',
  });

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:16 }}>
        <div>
          <div style={S.heading}>Dashboard</div>
          <div style={{ ...S.sub, marginBottom:0 }}>Visão executiva da empresa</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          {/* Atalhos de período */}
          <button onClick={aplicarSemanal} style={btnAtalho(ativoSem)}>
            Análise semanal: {_fmtDDMM(_semAtr)} até {_fmtDDMM(_hoje)}
          </button>
          <button onClick={aplicarMensal} style={btnAtalho(ativoMes)}>
            Análise mensal: {_fmtDDMM(_mesAtr)} até {_fmtDDMM(_hoje)}
          </button>
          {/* Inputs manuais — grupo compacto dd/mm/aaaa até dd/mm/aaaa */}
          <div style={{ display:'flex', alignItems:'center', background:'#0f1117', border:'1px solid #1a2035', borderRadius:8, overflow:'hidden' }}>
            <DatePickerInput value={inputInicio} onChange={setInputInicio} />
            <span style={{ color:'#475569', fontSize:12, padding:'0 2px', userSelect:'none' }}>até</span>
            <DatePickerInput value={inputFim} onChange={setInputFim} />
          </div>
          <button
            onClick={buscar}
            style={{ padding:'8px 16px', borderRadius:8, background:'rgba(96,165,250,0.12)', border:'1px solid rgba(96,165,250,0.3)', color:'#60a5fa', fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
          >
            Buscar
          </button>
          {/* Exportar */}
          <button
            onClick={handleExportarDados}
            disabled={exportando}
            style={{
              display:'flex', alignItems:'center', gap:8,
              background:'#f6c90e', color:'#0e1420', border:'none',
              borderRadius:8, padding:'9px 16px', fontSize:13, fontWeight:700,
              cursor: exportando ? 'default' : 'pointer', opacity: exportando ? 0.6 : 1,
              whiteSpace:'nowrap',
            }}
          >
            <Download size={16} />
            {exportando ? 'Exportando...' : 'Exportar dados'}
          </button>
        </div>
      </div>

      {erro && <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:16 }}>{erro}</div>}
      {!data && !erro && <div style={{ color:'#475569' }}>Carregando...</div>}

      {data && (
        <>
          <div style={{ ...S.kpiGrid, marginTop:8 }}>
            <KPI label="Total de Leads" value={data.total_leads} color="#60a5fa" icon={Target} />
            <KPI label="Vendas Fechadas" value={data.leads_fechados} color="#4ade80" icon={TrendingUp} />
            <KPI label="Receita Total" value={`R$ ${(data.receita_total/1000).toFixed(0)}k`} color="#f6c90e" icon={DollarSign} />
            <KPI label="Taxa de Conversão" value={`${data.taxa_conversao}%`} color="#fb923c" icon={Percent} />
          </div>

          <div style={S.chartsRow}>
            {/* Gráfico de barras + linha */}
            <div style={S.card}>
              <div style={{ ...S.cardTitle, marginBottom:4 }}>Leads por Mês</div>
              <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:12 }}>
                <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#94a3b8' }}>
                  <span style={{ display:'inline-block', width:12, height:12, borderRadius:3, background:'#f6c90e' }} />
                  Leads criados
                </span>
                <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#93c5fd' }}>
                  <span style={{ display:'inline-block', width:20, height:2, background:'#93c5fd', borderRadius:2 }} />
                  Vendas fechadas
                </span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={data.leads_por_mes}>
                  <XAxis dataKey="mes" tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BarLineTooltip />} />
                  <Bar dataKey="count" fill="#f6c90e" radius={[4,4,0,0]} name="Leads" />
                  <Line type="monotone" dataKey="fechados" stroke="#93c5fd" strokeWidth={2.5} dot={{ fill:'#93c5fd', r:3 }} activeDot={{ r:5 }} name="Vendas fechadas" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Donut com legenda top 5 */}
            <div style={S.card}>
              <div style={S.cardTitle}>Leads por Status (top 5)</div>
              <div style={{ display:'flex', alignItems:'center' }}>
                <div style={{ flex:'0 0 auto', width:180 }}>
                  <ResponsiveContainer width={180} height={200}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={48} outerRadius={78} dataKey="value">
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<PieTooltipContent />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ flex:1, paddingLeft:12 }}>
                  {pieData.map((d, i) => {
                    const pct = totalPie > 0 ? ((d.value / totalPie) * 100).toFixed(1) : 0;
                    return (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                        <div style={{ width:10, height:10, borderRadius:2, background:COLORS[i % COLORS.length], flexShrink:0 }} />
                        <div style={{ fontSize:11, color:'#94a3b8', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.name}</div>
                        <div style={{ fontSize:11, color:'#cbd5e1', fontWeight:700, whiteSpace:'nowrap' }}>{d.value} · {pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop:28 }}>
            <TopVendedores filtroInicio={filtroInicio} filtroFim={filtroFim} />
          </div>
        </>
      )}
    </div>
  );
}

function DashboardVendedor() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    api.get('/dashboard/vendedor/').then(r => setData(r.data)).catch(() => setErro('Erro ao carregar dados. Tente atualizar a página.'));
  }, []);
  if (erro) return <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13 }}>{erro}</div>;
  if (!data) return <div style={{ color:'#475569' }}>Carregando...</div>;

  const pieData = Object.entries(data.leads_por_status).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);

  return (
    <div>
      <div style={S.heading}>Olá, {user?.nome?.split(' ')[0]}! 👋</div>
      <div style={S.sub}>Seu desempenho de vendas</div>
      <div style={S.kpiGrid}>
        <KPI label="Meus Leads" value={data.total_leads} color="#60a5fa" icon={Target} />
        <KPI label="Vendas Fechadas" value={data.leads_fechados} color="#4ade80" icon={TrendingUp} />
        <KPI label="Minha Receita" value={`R$ ${(data.receita_total/1000).toFixed(0)}k`} color="#f6c90e" icon={DollarSign} />
        {data.meta && <KPI label="Meta do Mês" value={`${data.meta.percentual_atingido}%`} color="#fb923c" icon={Percent} />}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16 }}>
        <div style={S.card}>
          <div style={S.cardTitle}>Leads por Status</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip {...TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>Próximas Atividades</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {data.proximas_atividades.length === 0 && <div style={{ color:'#475569', fontSize:13 }}>Nenhuma atividade</div>}
            {data.proximas_atividades.map((a, i) => (
              <div key={i} style={{ padding:'9px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1a2035', fontSize:13 }}>
                <div style={{ color:'#cbd5e1', fontWeight:600 }}>{a.cliente_nome || a.titulo}</div>
                <div style={{ color:'#475569', fontSize:11, marginTop:2 }}>{new Date(a.data_hora).toLocaleString('pt-BR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardSimples({ title, sub, showPending, verificarPath = '/controle' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (showPending) {
      api.get('/dashboard/pending/').then(r => setPending(r.data)).catch(() => {});
    }
  }, [showPending]);

  return (
    <div>
      <div style={S.heading}>Olá, {user?.nome?.split(' ')[0]}! 👋</div>
      <div style={S.sub}>{sub}</div>

      {/* Notificação de trabalhos pendentes */}
      {showPending && pending && pending.count > 0 && (
        <div style={{ background:'rgba(246,201,14,0.08)', border:'1px solid rgba(246,201,14,0.25)', borderRadius:12, padding:'18px 22px', maxWidth:480, marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontSize:13, color:'#f6c90e', fontWeight:700, marginBottom:4 }}>
              {pending.count} trabalho{pending.count !== 1 ? 's' : ''} aguardando
            </div>
            <div style={{ fontSize:12, color:'#64748b' }}>{pending.label}</div>
          </div>
          <button
            onClick={() => navigate(verificarPath)}
            style={{ padding:'8px 18px', borderRadius:8, background:'rgba(246,201,14,0.2)', border:'1px solid rgba(246,201,14,0.4)', color:'#f6c90e', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            Verificar
          </button>
        </div>
      )}

      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:24, maxWidth:480 }}>
        <div style={{ color:'#94a3b8', fontSize:14, lineHeight:1.8 }}>{title}</div>
      </div>
    </div>
  );
}

function DashboardInstalador() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);

  useEffect(() => { api.get('/instalador/').then(r => setJobs(r.data)).catch(() => {}); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const jobsHoje   = jobs.filter(j => j.data_prevista === today || j.data_inicio === today);
  const jobsAtivos = jobs.filter(j => j.status !== 'instalacao_concluida');

  return (
    <div>
      <div style={S.heading}>Olá, {user?.nome?.split(' ')[0]}! 👋</div>
      <div style={S.sub}>Área do Instalador</div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:24, maxWidth:520 }}>
        <div style={{ ...S.kpi, borderTop:'3px solid #f6c90e' }}>
          <div style={S.kpiLabel}>Trabalhos para Hoje</div>
          <div style={{ ...S.kpiValue, color:'#f6c90e' }}>{jobsHoje.length}</div>
        </div>
        <div style={{ ...S.kpi, borderTop:'3px solid #60a5fa' }}>
          <div style={S.kpiLabel}>Em Andamento</div>
          <div style={{ ...S.kpiValue, color:'#60a5fa' }}>{jobsAtivos.length}</div>
        </div>
      </div>

      <button
        onClick={() => navigate('/clientes')}
        style={{ padding:'10px 22px', borderRadius:9, background:'rgba(246,201,14,0.15)', border:'1px solid rgba(246,201,14,0.4)', color:'#f6c90e', fontSize:14, fontWeight:600, cursor:'pointer' }}>
        Ver Minhas Instalações →
      </button>
    </div>
  );
}

export default function Dashboard() {
  const { user, isChefe, isVendedor, isGerenteComercial, isPreVenda, isPosVenda, isEngenheiro, isInstalador } = useAuth();
  if (isChefe() || isGerenteComercial()) return <DashboardChefe />;
  if (isVendedor()) return <DashboardVendedor />;
  if (isInstalador()) return <DashboardInstalador />;
  if (isPreVenda()) return <DashboardSimples title="Acesse o menu Pré Venda para cadastrar novos leads e distribuir para os vendedores." sub="Área de Pré Venda" />;
  if (isPosVenda()) return <DashboardSimples showPending title="Acesse a aba Controle para ver e atualizar os trabalhos do time de Pós Venda." sub="Área de Pós Venda" />;
  if (isEngenheiro()) return <DashboardSimples showPending verificarPath="/clientes" title="Acesse a aba Clientes para ver e atualizar os projetos de instalação." sub="Área de Engenharia" />;
  return null;
}
